import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineDuplicate, HiOutlineEye, HiOutlinePencil } from 'react-icons/hi';
import { MessageCompositionStudio, StudioMode } from './useMessageCompositionStudio';

interface StudioToolbarProps {
    studio: MessageCompositionStudio;
    mode: StudioMode;
    onSave: () => void;
    onCancel: () => void;
    onDuplicate: () => void;
    onToggleStatus: () => void;
    onOpenPreview: () => void;
}

const secondary = 'shrink-0 whitespace-nowrap px-4 py-2 rounded-md text-sm bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-100 dark:hover:bg-slate-600';

const StudioToolbar: React.FC<StudioToolbarProps> = ({ studio, mode, onSave, onCancel, onDuplicate, onToggleStatus, onOpenPreview }) => {
    const navigate = useNavigate();
    const { meta, dirty, saving, errors } = studio;

    if (!meta) {
        if (mode === 'new') {
            return (
                <div className="flex flex-nowrap items-center justify-end gap-2 m-2">
                    <button type="button" className={secondary} onClick={onCancel} disabled={saving}>
                        Cancelar
                    </button>
                </div>
            );
        }
        return null;
    }
    const active = meta.status === 'ACTIVE';

    return (
        <div className="flex flex-nowrap items-center justify-end gap-2 m-2">
            <button type="button" onClick={onOpenPreview} className={`${secondary} xl:hidden inline-flex items-center gap-1.5`}>
                <HiOutlineEye size={16} /> Pré-visualizar
            </button>
            {mode === 'view' ? (
                <>
                    <button type="button" onClick={onDuplicate} className={`${secondary} inline-flex items-center gap-1.5`}>
                        <HiOutlineDuplicate size={16} /> Duplicar composição
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate(`/communication/message-composition/${meta.id}/edit`)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                        <HiOutlinePencil size={16} /> Editar
                    </button>
                    <button
                        type="button"
                        onClick={onToggleStatus}
                        disabled={!active && errors.length > 0}
                        title={!active && errors.length > 0 ? 'Resolva as pendências antes de ativar' : undefined}
                        className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-md text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                            active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                        }`}
                    >
                        {active ? 'Desativar' : 'Ativar'}
                    </button>
                </>
            ) : (
                <>
                    <button type="button" className={secondary} onClick={onCancel} disabled={saving}>
                        {mode === 'edit' && dirty ? 'Descartar' : 'Cancelar'}
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={saving || (mode === 'edit' && !dirty)}
                        title={errors.length ? `${errors.length} pendência(s) a resolver` : ''}
                        className="relative shrink-0 px-5 py-2 rounded-md text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {mode === 'new' ? 'Criar composição' : 'Salvar'}
                        {errors.length > 0 && (
                            <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center">
                                {errors.length}
                            </span>
                        )}
                    </button>
                </>
            )}
        </div>
    );
};

export default StudioToolbar;
